/**
 * Drop-in for vue3-toastify's `toast`, rendering into the design-system stack.
 *
 * ~250 call sites use `this.$toast.success(msg, opts)` / `toast.error(...)`.
 * Rather than rewrite them, `$toast` (globalviariables.ts) and the few direct
 * imports now point here, and every message lands in <ToastStack />: bottom
 * right, 4.2 s, spec look.
 *
 * Option mapping from the toastify API:
 * - `autoClose: false`  -> persistent (stays until closed). Kept: those are
 *   errors or progress notes the user has to see.
 * - `autoClose: <ms>`   -> ignored. v3.3 sets one timing everywhere (4.2 s,
 *   7 s with Undo); the old 1.8/3/5 s values were per-screen accidents.
 * - `toastId`           -> replaces the toast with that id instead of stacking.
 * - `onClose`           -> called when it leaves.
 * - `position`, `color`, `theme`, ... -> ignored; the stack owns placement.
 *
 * New code can also pass the stack's own options (`description`, `undo`,
 * `action`) in the same second argument.
 */
import {
    pushToast,
    dismissToast,
    clearToasts,
    type ToastAction,
    type ToastId,
    type ToastType
} from '@/composables/useToastStack';

export interface ToastCallOptions {
    autoClose?: number | false | boolean;
    toastId?: ToastId;
    onClose?: () => void;
    type?: ToastType | 'default';
    description?: string;
    undo?: () => void;
    action?: ToastAction;
    // Anything else toastify accepted (position, color, theme, ...) is ignored.
    [key: string]: unknown;
}

/** Turn whatever a call site passed into readable text. */
export function toastText(content: unknown): string {
    if (content == null) return '';
    if (typeof content === 'string') return content;
    if (typeof content === 'number' || typeof content === 'boolean') return String(content);
    if (content instanceof Error) return content.message;
    if (typeof content === 'object') {
        const c = content as Record<string, unknown>;
        if (typeof c.message === 'string') return c.message;
        if (typeof c.response_message === 'string') return c.response_message;
    }
    try {
        return JSON.stringify(content);
    } catch {
        return String(content);
    }
}

function show(type: ToastType, content: unknown, opts: ToastCallOptions = {}): ToastId {
    return pushToast({
        title: toastText(content),
        description: opts.description,
        type,
        duration: opts.autoClose === false ? false : undefined,
        undo: opts.undo,
        action: opts.action,
        id: opts.toastId,
        onClose: opts.onClose
    });
}

type ToastFn = ((content: unknown, opts?: ToastCallOptions) => ToastId) & {
    success: (content: unknown, opts?: ToastCallOptions) => ToastId;
    error: (content: unknown, opts?: ToastCallOptions) => ToastId;
    warning: (content: unknown, opts?: ToastCallOptions) => ToastId;
    warn: (content: unknown, opts?: ToastCallOptions) => ToastId;
    info: (content: unknown, opts?: ToastCallOptions) => ToastId;
    remove: (id?: ToastId) => void;
    clearAll: () => void;
};

const base = (content: unknown, opts: ToastCallOptions = {}) => {
    const t = opts.type && opts.type !== 'default' ? opts.type : 'info';
    return show(t, content, opts);
};

export const toast: ToastFn = Object.assign(base, {
    success: (c: unknown, o?: ToastCallOptions) => show('success', c, o),
    error: (c: unknown, o?: ToastCallOptions) => show('error', c, o),
    warning: (c: unknown, o?: ToastCallOptions) => show('warning', c, o),
    warn: (c: unknown, o?: ToastCallOptions) => show('warning', c, o),
    info: (c: unknown, o?: ToastCallOptions) => show('info', c, o),
    remove: (id?: ToastId) => (id == null ? clearToasts() : dismissToast(id)),
    clearAll: () => clearToasts()
});

export default toast;
